import React, { useState, useEffect, useRef } from 'react';
import './DMItem.css';

function presenceStatus(lastSeenAt, presenceOverride) {
  // If explicitly offline
  if (presenceOverride === 'offline') return { status: 'offline', label: 'Offline' };

  if (!lastSeenAt) return { status: 'offline', label: 'Offline' };

  const now = Date.now();
  const lastSeen = new Date(lastSeenAt).getTime();
  const diffMs = now - lastSeen;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 2) return { status: 'active', label: 'Active' };
  if (diffMin < 60) return { status: 'inactive', label: `Active ${diffMin}m ago` };
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return { status: 'inactive', label: `Active ${diffHr}h ago` };
  return { status: 'offline', label: 'Offline' };
}

function DMItem({
  conversation,
  displayName,
  isGroupDm = false,
  groupMemberCount = 0,
  isSelected,
  onSelect,
  unreadCount = 0,
  onToggleFavorite,
  onToggleAI,
  onArchive,
  onHide,
  isFavorited,
  userPresence
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const presence = userPresence
    ? presenceStatus(userPresence.lastSeenAt, userPresence.presenceOverride)
    : { status: 'offline', label: 'Offline' };

  const statusMessage = userPresence?.statusMessage || '';

  return (
    <div className={`dm-item-row ${isSelected ? 'selected' : ''}`}>
      <button
        type="button"
        className={`dm-item ${unreadCount > 0 ? 'dm-item-unread' : ''}`}
        onClick={() => onSelect(conversation)}
        aria-pressed={isSelected}
      >
        {isGroupDm ? (
          <span className="dm-group-icon" aria-hidden="true">
            <span className="dm-group-icon-primary" />
            <span className="dm-group-icon-secondary" />
          </span>
        ) : (
          <span className={`dm-dot dm-dot-${presence.status}`} title={presence.label} />
        )}
        <span className="dm-content">
          <span className="dm-name-row">
            <span
              className={`dm-name ${unreadCount > 0 ? 'dm-name-unread' : ''}`}
              title={displayName || conversation.title || 'Untitled'}
            >
              {displayName || conversation.title || 'Untitled'}
            </span>
            {conversation.aiEnabled && (
              <span className="channel-ai-indicator" aria-label="AI enabled" role="img">
                AI
              </span>
            )}
            {unreadCount > 0 && (
              <span className="unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </span>
          {isGroupDm && (
            <span className="dm-meta">
              {groupMemberCount > 0 ? `${groupMemberCount} members` : 'Group direct message'}
            </span>
          )}
          {!isGroupDm && (presence.status !== 'active' || statusMessage) && (
            <span className="dm-meta">
              {statusMessage && <span className="dm-status-text">{statusMessage}</span>}
              {!statusMessage && presence.status !== 'active' && (
                <span className="dm-presence-label">{presence.label}</span>
              )}
            </span>
          )}
        </span>
      </button>
      <div className="dm-item-actions" style={{ position: 'relative' }}>
        <button
          type="button"
          className="item-context-btn"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
          aria-label={`Actions for ${displayName}`}
          title="More actions"
        >
          <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
            <path d="M3 9.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
          </svg>
        </button>
        {menuOpen && (
          <div className="context-menu" ref={menuRef} role="menu">
            {onToggleFavorite && (
              <button
                type="button"
                className="context-menu-item"
                role="menuitem"
                onClick={() => { setMenuOpen(false); onToggleFavorite(conversation.id); }}
              >
                {isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}
              </button>
            )}
            {onToggleAI && conversation.type !== 'AI' && (
              <>
                <hr className="context-menu-separator" />
                <button
                  type="button"
                  className="context-menu-item context-menu-ai-toggle"
                  role="menuitemcheckbox"
                  aria-checked={!!conversation.aiEnabled}
                  onClick={() => {
                    setMenuOpen(false);
                    onToggleAI(conversation.id);
                  }}
                >
                  <span>{conversation.aiEnabled ? 'Disable AI' : 'Enable AI'}</span>
                  <span
                    className={`context-menu-ai-dot ${conversation.aiEnabled ? 'active' : ''}`}
                    aria-hidden="true"
                  />
                </button>
              </>
            )}
            {onHide && !isGroupDm && (
              // Per-user soft hide. NOT a delete — the peer still sees
              // the conversation and all messages. We call this "Hide
              // chat" to match the data model and avoid implying any
              // GDPR-style erasure. If a new message arrives, the
              // conversation reappears in the sidebar with only the
              // new content visible (historyStartAt cutoff preserved).
              <button
                type="button"
                className="context-menu-item"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  const descriptor = `Hide your view of the conversation with ${displayName}? The other person will still see it.`;
                  if (window.confirm(descriptor)) {
                    onHide(conversation.id);
                  }
                }}
              >
                Hide chat
              </button>
            )}
            {onArchive && (
              <button
                type="button"
                className="context-menu-item context-menu-item-danger"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  const descriptor = isGroupDm
                    ? `Delete group conversation "${displayName}"?`
                    : `Delete conversation with ${displayName}?`;
                  if (window.confirm(descriptor)) {
                    onArchive(conversation.id);
                  }
                }}
              >
                Delete conversation
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DMItem;
